import React, { useState } from 'react';
import { landlordAPI } from '../../../lib/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Loader2, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const initialForm = () => ({
  amount: '', payment_date: new Date().toISOString().split('T')[0],
  payment_method: 'cash', payment_type: 'rent',
  period_month: new Date().toISOString().slice(0, 7),
  reference_number: '', notes: ''
});

export const RecordPaymentDialog = ({ open, onOpenChange, tenantId, tenantType, tenantName }) => {
  const [paymentForm, setPaymentForm] = useState(initialForm());
  const [paymentProof, setPaymentProof] = useState(null);
  const [savingPayment, setSavingPayment] = useState(false);

  const handleSavePayment = async () => {
    if (!paymentForm.amount) { toast.error('Amount is required'); return; }
    setSavingPayment(true);
    try {
      const formData = new FormData();
      formData.append('tenant_id', tenantId);
      formData.append('tenant_type', tenantType);
      formData.append('amount', paymentForm.amount);
      formData.append('payment_date', paymentForm.payment_date);
      formData.append('payment_method', paymentForm.payment_method);
      formData.append('payment_type', paymentForm.payment_type);
      if (paymentForm.period_month) formData.append('period_month', paymentForm.period_month);
      if (paymentForm.reference_number) formData.append('reference_number', paymentForm.reference_number);
      if (paymentForm.notes) formData.append('notes', paymentForm.notes);
      if (paymentProof) formData.append('proof_file', paymentProof);
      await landlordAPI.createManualPayment(formData);
      toast.success('Payment recorded');
      onOpenChange(false);
      setPaymentForm(initialForm());
      setPaymentProof(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally { setSavingPayment(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>For: {tenantName}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} data-testid="pay-amount" />
            </div>
            <div className="space-y-2">
              <Label>Date</Label>
              <Input type="date" value={paymentForm.payment_date} onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} data-testid="pay-date" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={paymentForm.payment_type} onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rent">Rent</SelectItem>
                  <SelectItem value="deposit">Deposit</SelectItem>
                  <SelectItem value="advance">Advance</SelectItem>
                  <SelectItem value="utility">Utility</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Method</Label>
              <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="gcash">GCash</SelectItem>
                  <SelectItem value="maya">Maya</SelectItem>
                  <SelectItem value="bank">Bank</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Period</Label>
              <Input type="month" value={paymentForm.period_month} onChange={(e) => setPaymentForm({ ...paymentForm, period_month: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Ref #</Label>
              <Input value={paymentForm.reference_number} onChange={(e) => setPaymentForm({ ...paymentForm, reference_number: e.target.value })} placeholder="Reference #" data-testid="pay-ref" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Proof of Payment</Label>
            <Input type="file" accept="image/*" onChange={(e) => setPaymentProof(e.target.files[0])} data-testid="pay-proof" />
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={paymentForm.notes} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} rows={2} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSavePayment} disabled={savingPayment} className="bg-[#C85A42] hover:bg-[#B54A35]" data-testid="save-payment-btn">
              {savingPayment ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
              Record
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
